import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { ShieldIcon, ShieldXIcon, MailIcon } from "lucide-react";
import { requestReviewerOTP, verifyReviewerOTP } from "@service/reviewer";
import BasicLayout from "@container/layout/basic";
import { isValidEmail } from "@util/string";
import { setCookie } from "@util/cookie";

type Screen = "email" | "otp";

export default function ReviewerVerifyPage() {
    const [searchParams] = useSearchParams();
    const nextUrl = searchParams.get("next") || "";

    const [screen, setScreen] = useState<Screen>("email");
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [isSending, setIsSending] = useState(false);

    const [otpValues, setOtpValues] = useState<string[]>(Array(6).fill(""));
    const [otpError, setOtpError] = useState("");
    const [isVerifying, setIsVerifying] = useState(false);
    const [isLocked, setIsLocked] = useState(false);

    const [timeLeft, setTimeLeft] = useState(59);
    const [isResending, setIsResending] = useState(false);

    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        if (screen === "otp" && timeLeft > 0) {
            const timer = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [screen, timeLeft]);

    const handleSendOTP = async () => {
        const trimmed = email.trim();
        if (!isValidEmail(trimmed)) {
            setEmailError("Please enter a valid email address.");
            return;
        }
        setIsSending(true);
        setEmailError("");
        try {
            const resp = await requestReviewerOTP({ email: trimmed });
            if (resp.code === 200) {
                setScreen("otp");
                setTimeLeft(59);
            } else if (resp.code === 429) {
                const waitSec = resp.data?.retry_after_seconds ?? 60;
                setEmailError(`Please wait ${waitSec} second${waitSec !== 1 ? "s" : ""} before requesting another code.`);
            } else {
                setEmailError(
                    "This email is not registered as a reviewer. Try another email or contact your HR team to be added."
                );
            }
        } catch {
            setEmailError(
                "This email is not registered as a reviewer. Try another email or contact your HR team to be added."
            );
        } finally {
            setIsSending(false);
        }
    };

    const handleOTPChange = (index: number, value: string) => {
        if (!/^\d*$/.test(value)) return;
        const next = [...otpValues];
        next[index] = value;
        setOtpValues(next);
        if (otpError) setOtpError("");
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleOTPKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Backspace" && !otpValues[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handleOTPPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text/plain").trim();
        if (/^\d{6}$/.test(pasted)) {
            setOtpValues(pasted.split(""));
            inputRefs.current[5]?.focus();
        }
    };

    const handleVerify = async () => {
        const otp = otpValues.join("");
        if (otp.length !== 6) {
            setOtpError("Please enter all 6 digits.");
            return;
        }
        setIsVerifying(true);
        setOtpError("");
        try {
            const resp = await verifyReviewerOTP({
                email: email.trim(),
                otp,
            });
            if (resp.code === 200) {
                if (resp.data?.token) {
                    setCookie("REVIEWER_TOKEN", resp.data.token, resp.data.session_duration_days ?? 30);
                }
                window.location.href = nextUrl || "/";
            } else if (resp.errors?.[0] === "TOO_MANY_ATTEMPTS") {
                setIsLocked(true);
                setOtpError("Too many attempts. Request a new code.");
                setTimeLeft(0);
            } else {
                const remaining = resp.data?.attempts_remaining;
                setOtpError(
                    `Incorrect code.${remaining != null ? ` ${remaining} attempt${remaining !== 1 ? "s" : ""} remaining.` : ""}`
                );
                setOtpValues(Array(6).fill(""));
                setTimeout(() => inputRefs.current[0]?.focus(), 0);
            }
        } catch {
            setOtpError("Something went wrong. Please try again.");
        } finally {
            setIsVerifying(false);
        }
    };

    const handleResend = async () => {
        setIsResending(true);
        setOtpError("");
        try {
            const resp = await requestReviewerOTP({ email: email.trim() });
            const waitSec = resp.code === 429 ? (resp.data?.retry_after_seconds ?? 60) : 59;
            setTimeLeft(waitSec);
            setIsLocked(false);
            setOtpValues(Array(6).fill(""));
            setTimeout(() => inputRefs.current[0]?.focus(), 0);
        } catch {
            setOtpError("Failed to resend code. Please try again.");
        } finally {
            setIsResending(false);
        }
    };

    return (
        <BasicLayout hideNavbarMenu isHideLoginButton showFooter={false}>
            <div className="w-full max-w-md mx-auto px-4 py-10">
                <div className="border border-gray-200 rounded-xl shadow-sm bg-white px-8 py-10">
                {screen === "email" ? (
                    <div className="flex flex-col items-center text-center space-y-6">
                        <div className={`p-4 rounded-full ${emailError ? "bg-red-50" : "bg-blue-50"}`}>
                            {emailError ? (
                                <ShieldXIcon className="h-10 w-10 text-red-500" />
                            ) : (
                                <ShieldIcon className="h-10 w-10 text-blue-500" />
                            )}
                        </div>
                        <div className="space-y-1">
                            <h1 className="text-2xl font-bold text-gray-900">Verify your identity</h1>
                            <p className="text-sm text-gray-500">
                                Enter your work email to continue.
                            </p>
                        </div>
                        <div className="w-full space-y-2 text-left">
                            <div className="relative">
                                <Input
                                    type="email"
                                    placeholder="Your work email address"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        if (emailError) setEmailError("");
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") handleSendOTP();
                                    }}
                                    className={`pl-10 ${emailError ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                                    disabled={isSending}
                                    autoFocus
                                />
                                <MailIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            </div>
                            {emailError ? (
                                <p className="text-xs text-red-600">{emailError}</p>
                            ) : (
                                <p className="text-xs text-gray-500">
                                    Your email must be registered as a reviewer by your HR team.
                                </p>
                            )}
                        </div>
                        <Button
                            className="w-full bg-blue-600 hover:bg-blue-700"
                            onClick={handleSendOTP}
                            disabled={!isValidEmail(email.trim()) || isSending}
                        >
                            {isSending ? "Sending..." : "Send verification code"}
                        </Button>
                    </div>
                ) : (
                    <div className="flex flex-col items-center text-center space-y-6">
                        <div className="p-4 rounded-full bg-blue-50">
                            <MailIcon className="h-10 w-10 text-blue-500" />
                        </div>
                        <div className="space-y-1">
                            <h1 className="text-2xl font-bold text-gray-900">Check your inbox</h1>
                            <p className="text-sm text-gray-500">
                                We sent a 6-digit code to{" "}
                                <span className="font-medium text-gray-900">{email}</span>
                            </p>
                        </div>
                        <div className="flex gap-2 justify-center">
                            {otpValues.map((val, i) => (
                                <input
                                    key={i}
                                    ref={(el) => {
                                        inputRefs.current[i] = el;
                                    }}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    value={val}
                                    onChange={(e) => handleOTPChange(i, e.target.value)}
                                    onKeyDown={(e) => handleOTPKeyDown(i, e)}
                                    onPaste={i === 0 ? handleOTPPaste : undefined}
                                    disabled={isLocked || isVerifying}
                                    autoFocus={i === 0}
                                    className={`w-12 h-12 text-center text-xl font-bold rounded-md border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                        otpError
                                            ? "border-red-400 bg-red-50 text-red-700"
                                            : "border-gray-300 bg-white text-gray-900"
                                    }`}
                                />
                            ))}
                        </div>
                        {otpError && <p className="text-sm text-red-600">{otpError}</p>}
                        <Button
                            className="w-full bg-blue-600 hover:bg-blue-700"
                            onClick={handleVerify}
                            disabled={otpValues.some((v) => v === "") || isVerifying || isLocked}
                        >
                            {isVerifying ? "Verifying..." : "Verify"}
                        </Button>
                        <div className="space-y-1 text-sm text-gray-500">
                            {timeLeft > 0 && !isLocked ? (
                                <p>Didn't receive it? Resend code ({timeLeft}s)</p>
                            ) : (
                                <p>
                                    <button
                                        onClick={handleResend}
                                        disabled={isResending}
                                        className="text-blue-600 hover:underline"
                                    >
                                        {isResending ? "Resending..." : "Resend code"}
                                    </button>
                                </p>
                            )}
                            <p>
                                <button
                                    onClick={() => {
                                        setScreen("email");
                                        setOtpValues(Array(6).fill(""));
                                        setOtpError("");
                                    }}
                                    className="text-blue-600 hover:underline"
                                >
                                    Wrong email? Go back
                                </button>
                            </p>
                        </div>
                    </div>
                )}
                </div>
            </div>
        </BasicLayout>
    );
}
